import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useSubjectChapter } from '@/context/SubjectChapterContext';
import { usePoints } from '@/context/PointsContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { subjectsAPI, chaptersAPI } from '@/api/curriculum';
import { getPortalType, getUserData } from '@/utils/auth';
import { getAnonymousUserPreferences } from '@/utils/anonymousAuth';
import { cn } from '@/lib/utils';
import { Trophy, Diamond, Flame, Heart, X, Sparkles, BookOpen, Menu, ChevronDown, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications } from '@/hooks/useNotifications';
import NotificationList from '@/components/Notifications/NotificationList';
import { Bell } from 'lucide-react';
import { useUserProfile } from '@/context/UserProfileContext';
import { usePreferences } from '@/context/PreferencesContext';
import { isBudleeContentViewerEnabledForSelection } from '@/utils/v2LearnRollout';
import { formatBoardLabel } from '@/utils/boardUtils';

// Stats Trigger Component - Simplified to only Trophy + XP with Desktop Animation
function StatsTrigger({ onClick, xp }) {
    return (
        <Button
            variant="ghost"
            size="icon"
            onClick={onClick}
            className="relative w-9 h-9 rounded-full p-0 overflow-visible hover:bg-transparent active:scale-95 transition-transform"
        >
            {/* Background Glow - matches desktop pulsate */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-400 via-yellow-500 to-amber-600 animate-pulse" style={{ animationDuration: '1s' }} />

            {/* The Main Circle */}
            <div className="relative w-full h-full rounded-full bg-gradient-to-br from-yellow-400 via-yellow-500 to-amber-600 flex items-center justify-center shadow-lg border-2 border-white/20">
                <Trophy className="w-3 h-3 text-[hsl(var(--top-bar-foreground))] drop-shadow-lg animate-bounce" style={{ animationDuration: '1s', animationIterationCount: 'infinite' }} />

                {/* Constant sparkle effect for XP icon */}
                <>
                    <Sparkles className="absolute -top-0.5 -right-0.5 w-2 h-2 text-yellow-300 animate-ping" style={{ animationDuration: '3s' }} />
                    <Sparkles className="absolute -bottom-0.5 -left-0.5 w-1.5 h-1.5 text-amber-300 animate-ping" style={{ animationDelay: '1.5s', animationDuration: '3s' }} />
                </>

                {/* Rotating glow ring */}
                <div className="absolute inset-0 rounded-full border border-yellow-300/50 animate-spin" style={{ animationDuration: '5s' }} />

                {/* Level/Badge Bubble (XP Value) - Dynamic width for 3 digits */}
                <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#0F1F1A] rounded-full border border-yellow-400 flex items-center justify-center shadow-md">
                    <span className="text-[8px] font-black text-white leading-none mb-[1px]">
                        {xp}
                    </span>
                </div>
            </div>
        </Button>
    );
}

// Floating Stats Card
function FloatingStatsBar({ points }) {
    const xp = points?.total_xp ?? 0;
    const gems = points?.gems ?? 0;
    const streaks = points?.current_streak ?? 0;
    const hearts = points?.hearts ?? 5;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.8, y: -20, x: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20, x: 20 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="absolute top-14 right-2 z-[15500] w-[210px]"
        >
            <Card className="bg-white/95 dark:bg-[#0F1F1A]/95 backdrop-blur-xl border border-gray-200 dark:border-[hsl(var(--primary))]/30 shadow-2xl rounded-2xl overflow-hidden">
                <CardContent className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-2">
                        {[
                            { icon: Trophy, color: 'text-yellow-500', value: xp, label: 'XP' },
                            { icon: Diamond, color: 'text-purple-500', value: gems, label: 'Gems' },
                            { icon: Flame, color: 'text-orange-500', value: streaks, label: 'Streak' },
                            { icon: Heart, color: 'text-rose-500', value: hearts, label: 'Hearts', fill: true }
                        ].map((stat, i) => (
                            <div key={i} className="flex flex-col items-center p-2.5 bg-gray-50 dark:bg-white/[0.03] rounded-xl border border-gray-100 dark:border-white/5 transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.06]">
                                <stat.icon className={cn("w-5 h-5 mb-1", stat.color, stat.fill && "fill-current")} />
                                <span className="text-sm font-bold text-gray-900 dark:text-white tracking-tight">{stat.value}</span>
                                <span className="text-[9px] text-gray-400 dark:text-white/30 uppercase font-bold tracking-wider">{stat.label}</span>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}

// Integrated Popout Component
function StatsPopout() {
    const { points, loading } = usePoints();
    const [isOpen, setIsOpen] = useState(false);

    if (loading && !points) return (
        <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-[#0F1F1A] border border-gray-200 dark:border-[hsl(var(--card-border))] animate-pulse" />
    );

    return (
        <div className="relative">
            <StatsTrigger onClick={() => setIsOpen(!isOpen)} xp={points?.total_xp ?? 0} />

            <AnimatePresence>
                {isOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[15400] bg-black/5"
                            onClick={() => setIsOpen(false)}
                        />
                        <FloatingStatsBar
                            points={points}
                        />
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function MobileTopBar({ onOpenSidebar, onCallClick, isCallDisabled, isDisabled }) {
    const { config } = usePreferences();
    const location = useLocation();
    const {
        selectedSubject,
        selectedChapter,
        selectedSubjectId,
        selectedChapterId,
        selectedBoardId,
        selectedBoard,
        selectedGradeId,
        selectedGrade,
        updateSubject,
        updateChapter,
        updateBoard,
        updateGrade,
        subjects,
        chapters,
        boards,
        grades,
        loadingSubjects,
        loadingChapters,
        loadingBoards,
        loadingGrades,
        boardId,
        gradeId,
        isPdfViewerOpen
    } = useSubjectChapter();
    const { profile } = useUserProfile();


    const pathname = location.pathname;
    const isGuest = localStorage.getItem('is_guest_session') === 'true';
    const effectiveCallDisabled = !isGuest && isCallDisabled;
    const isStudent = pathname.startsWith('/student');
    const isTeacher = pathname.startsWith('/teacher') || window.location.hash.includes('/teacher') || getPortalType(pathname) === 'teacher';
    const userData = getUserData(getPortalType(pathname));
    const { unreadCount, refreshUnreadCount } = useNotifications(false);
    const [isNotifOpen, setIsNotifOpen] = useState(false);

    const isV2Enabled = useMemo(() => {
        return isBudleeContentViewerEnabledForSelection({
            config,
            boardValues: [selectedBoardId, selectedBoard, boardId],
            gradeValues: [selectedGradeId, selectedGrade, gradeId],
            subjectValues: [selectedSubjectId, selectedSubject],
            chapterValues: [selectedChapterId, selectedChapter],
        });
    }, [config, selectedBoardId, selectedBoard, boardId, selectedGradeId, selectedGrade, gradeId, selectedSubjectId, selectedSubject, selectedChapterId, selectedChapter]);

    // Only show selector on specific student and teacher pages
    const showSelector = !(isV2Enabled && isPdfViewerOpen) && ((isStudent && (
        pathname.includes('/learn') ||
        pathname.includes('/chat') ||
        pathname.includes('/quests') ||
        pathname === '/student/curiosity'
    )) || (isTeacher && pathname.includes('/teach')));

    const [isSelectionExpanded, setIsSelectionExpanded] = useState(false);
    const isUserOpenedRef = React.useRef(false);

    // Check if a valid selection exists either in React state or in localStorage
    const hasStoredSelection = useMemo(() => {
        try {
            const stored = localStorage.getItem('student_subject_chapter_selection');
            if (stored) {
                const parsed = JSON.parse(stored);
                return !!(parsed?.selectedChapterId && parsed?.selectedSubjectId);
            }
        } catch {}
        return false;
    }, []);

    const hasFocus = !!(selectedSubjectId && selectedChapterId) || hasStoredSelection;
    const canClose = hasFocus || (!loadingSubjects && subjects.length === 0);

    // Body scroll lock
    useEffect(() => {
        if (isSelectionExpanded) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [isSelectionExpanded]);

    // If modal was auto-opened, close it once a focus is established
    useEffect(() => {
        if (isSelectionExpanded && !isUserOpenedRef.current && (selectedSubjectId && selectedChapterId)) {
            setIsSelectionExpanded(false);
        }
    }, [isSelectionExpanded, selectedSubjectId, selectedChapterId]);

    // Only force expansion if:
    // 1) Not already selected (neither in state nor in localStorage)
    // 2) Curriculum loading is fully settled (!loadingSubjects && !loadingChapters)
    // 3) There are subjects available
    useEffect(() => {
        if (!showSelector || hasFocus || loadingSubjects || loadingChapters) {
            return;
        }

        const timer = setTimeout(() => {
            if (showSelector && !hasFocus && !loadingSubjects && !loadingChapters && subjects.length > 0 && !isSelectionExpanded) {
                setIsSelectionExpanded(true);
            }
        }, 1500);
        return () => clearTimeout(timer);
    }, [showSelector, hasFocus, loadingSubjects, loadingChapters, subjects.length, isSelectionExpanded]);

    // Robust selector label with lookup fallbacks to prevent "Focus" showing when items are selected
    const selectorLabel = useMemo(() => {
        // 1. Prefer the explicit chapter name label from context
        if (selectedChapter) return selectedChapter;

        // 2. Fallback: Lookup chapter name in the chapters list if ID exists
        if (selectedChapterId && chapters?.length > 0) {
            const chap = chapters.find(c => String(c.id) === String(selectedChapterId));
            if (chap?.label) return chap.label;
        }

        // 3. Fallback to subject name if no chapter is selected
        if (selectedSubject) return selectedSubject;

        // 4. Fallback: Lookup subject name in the subjects list if ID exists
        if (selectedSubjectId && subjects?.length > 0) {
            const sub = subjects.find(s => String(s.id) === String(selectedSubjectId));
            if (sub?.label) return sub.label;
        }

        return 'Focus';
    }, [selectedChapter, selectedSubject, selectedChapterId, selectedSubjectId, chapters, subjects]);


    // Close on escape
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && canClose) {
                isUserOpenedRef.current = false;
                setIsSelectionExpanded(false);
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [canClose]);

    if (typeof document === 'undefined') return null;

    return createPortal(
        <>
            <div className={cn(
                "lg:hidden fixed top-0 left-0 right-0 h-[calc(3.5rem+max(env(safe-area-inset-top,0px),var(--safe-area-inset-top,0px)))] bg-[hsl(var(--main-bg))] border-b border-[hsl(var(--card-border))] w-full pt-safe-top z-[12000] flex-shrink-0",
                isDisabled && "pointer-events-none opacity-50"
            )}>
                <div className="flex items-center justify-between pl-1 pr-2 h-14">
                    <div className="flex items-center shrink-0">
                        <div className="relative flex flex-col items-center justify-center ml-1 h-14 w-16 sm:w-20 cursor-pointer" onClick={() => setIsNotifOpen(!isNotifOpen)}>
                            <img src="/logo.png" alt="Budlee" className="h-10 w-10 sm:h-11 sm:w-11 object-contain mb-1" />
                            {unreadCount > 0 && (
                                <div className="absolute top-1 right-2 min-w-[20px] h-[20px] px-1 bg-red-500 rounded-full border-2 border-[hsl(var(--main-bg))] flex items-center justify-center shadow-md animate-bounce transform origin-center scale-100">
                                    <span className="text-[10px] font-black text-white leading-none">
                                        {unreadCount > 99 ? '99+' : unreadCount}
                                    </span>
                                </div>
                            )}
                            {profile?.institute?.name && (
                                <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex items-center justify-center max-w-[70px] sm:max-w-[85px] leading-none select-none gap-0.5 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))]/80 px-1 py-0.5 rounded shadow-sm scale-90 origin-bottom">
                                    <span className="text-[7.5px] sm:text-[8px] italic font-medium text-[hsl(var(--teal-vivid))] leading-none">For</span>
                                    <span className="text-[8px] sm:text-[8.5px] font-black uppercase text-foreground truncate tracking-wide leading-none">
                                        {profile.institute.name}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Notification Dropdown Overlay for Mobile */}
                        <AnimatePresence>
                            {isNotifOpen && (
                                <>
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="fixed inset-0 z-[16000] bg-black/20"
                                        onClick={() => setIsNotifOpen(false)}
                                    />
                                    <motion.div
                                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                                        className="fixed top-[calc(3.5rem+max(env(safe-area-inset-top,0px),var(--safe-area-inset-top,0px)))] left-4 right-4 z-[16100] sm:left-auto sm:right-4 pointer-events-none"
                                    >
                                        <NotificationList 
                                            onClose={() => setIsNotifOpen(false)} 
                                            onRefreshCount={refreshUnreadCount} 
                                        />
                                    </motion.div>
                                </>
                            )}
                        </AnimatePresence>
                    </div>

                    {showSelector && (
                        <div className="flex-1 flex justify-center px-1 min-w-0 mx-1">
                            <div className="flex items-center bg-[hsl(var(--primary))]/30 rounded-full border border-[hsl(var(--primary))]/60 h-8 shadow-sm font-bold min-w-0 max-w-[200px] overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => {
                                        isUserOpenedRef.current = true;
                                        setIsSelectionExpanded(true);
                                    }}
                                    className="flex items-center gap-1 pl-3 pr-1.5 h-full hover:bg-black/5 dark:hover:bg-white/5 active:bg-black/10 transition-colors min-w-0 flex-1 text-left"
                                >
                                    <span className="truncate text-[10px] font-black uppercase tracking-wider text-[hsl(var(--foreground))]">
                                        {selectorLabel}
                                    </span>
                                    <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-100 text-[hsl(var(--foreground))]" />
                                </button>

                                {config?.voice_assistant_enabled && (
                                    <div className="px-1.5 flex-shrink-0 flex items-center">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onCallClick();
                                            }}
                                            disabled={effectiveCallDisabled}
                                            className={cn(
                                                "w-6 h-6 rounded-full text-white flex items-center justify-center shadow-md active:scale-95 transition-transform relative",
                                                effectiveCallDisabled
                                                    ? 'bg-slate-700 text-slate-400 cursor-not-allowed opacity-50'
                                                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                                            )}
                                            title={effectiveCallDisabled ? "Microphone permission required" : ""}
                                            aria-label="Call AI Tutor"
                                        >
                                            <Phone className="w-2.5 h-2.5 text-white" />
                                            <span className="absolute -top-0.5 -right-0.5 z-10 text-[5px] font-black text-emerald-400 border border-emerald-500/40 bg-slate-950 px-0.5 rounded-full shadow pointer-events-none h-2.5 min-w-[10px] flex items-center justify-center leading-none">
                                                AI
                                            </span>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {!isTeacher && (
                        <div className="shrink-0 flex items-center mr-0 overflow-visible">
                            <StatsPopout />
                        </div>
                    )}
                </div>
            </div>

            {/* Selector Modal Overlay */}
            {isSelectionExpanded && (
                <div
                    className="fixed inset-0 z-[15500] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 lg:hidden"
                    onClick={() => {
                        if (canClose) {
                            isUserOpenedRef.current = false;
                            setIsSelectionExpanded(false);
                        }
                    }}
                >
                    <div className="w-full max-w-sm" onClick={e => e.stopPropagation()}>
                        <Card className="bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] border-0 shadow-2xl relative rounded-3xl">
                            {/* Decorative background icons */}
                            <Sparkles className="absolute -top-4 -right-4 w-24 h-24 text-white/10 rotate-12 pointer-events-none" />
                            <BookOpen className="absolute -bottom-4 -left-4 w-20 h-20 text-white/5 -rotate-12 pointer-events-none" />

                            <CardContent className="p-4 sm:p-5 space-y-4">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-sm font-bold text-white">
                                        Select Focus
                                    </h2>
                                    {canClose && (
                                        <button
                                            onClick={() => {
                                                isUserOpenedRef.current = false;
                                                setIsSelectionExpanded(false);
                                            }}
                                            className="p-1 rounded-full bg-white/10 text-white/80 hover:text-white active:scale-95 transition-all"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>

                                <div className="space-y-3">
                                    {isTeacher && (
                                        <>
                                            <div className="space-y-1.5 overflow-visible">
                                                <label className="text-[10px] uppercase font-black text-white/70 ml-1 tracking-wider">Board</label>
                                                <Select
                                                    value={selectedBoardId ? selectedBoardId.toString() : ''}
                                                    onValueChange={(val) => {
                                                        const found = boards.find(b => String(b.id) === val);
                                                        updateBoard(val, found ? (found.board_code || found.label) : '');
                                                    }}
                                                    disabled={loadingBoards}
                                                >
                                                    <SelectTrigger className="w-full h-10 bg-white dark:bg-black/20 border-0 dark:border dark:border-white/10 text-black dark:text-white rounded-xl font-bold shadow-sm focus:ring-0 focus:ring-offset-0 text-xs">
                                                        <SelectValue placeholder={loadingBoards ? "Loading..." : "Select Board"} />
                                                    </SelectTrigger>
                                                    <SelectContent className="z-[20000] bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-100 dark:border-white/10 rounded-xl shadow-xl max-h-[220px] overflow-y-auto">
                                                        {boards.map(b => (
                                                            <SelectItem key={b.id} value={b.id.toString()} className="font-semibold py-2.5 border-b border-gray-50 dark:border-white/5 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 text-xs text-black dark:text-white">
                                                                {formatBoardLabel(b)}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <div className="space-y-1.5 overflow-visible">
                                                <label className="text-[10px] uppercase font-black text-white/70 ml-1 tracking-wider">Grade</label>
                                                <Select
                                                    value={selectedGradeId ? selectedGradeId.toString() : ''}
                                                    onValueChange={(val) => {
                                                        const found = grades.find(g => String(g.id) === val);
                                                        updateGrade(val, found ? found.label : '');
                                                    }}
                                                    disabled={!selectedBoardId || loadingGrades}
                                                >
                                                    <SelectTrigger className="w-full h-10 bg-white dark:bg-black/20 border-0 dark:border dark:border-white/10 text-black dark:text-white rounded-xl font-bold shadow-sm focus:ring-0 focus:ring-offset-0 text-xs">
                                                        <SelectValue placeholder={loadingGrades ? "Loading..." : "Select Grade"} />
                                                    </SelectTrigger>
                                                    <SelectContent className="z-[20000] bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-100 dark:border-white/10 rounded-xl shadow-xl max-h-[220px] overflow-y-auto">
                                                        {grades.map(g => (
                                                            <SelectItem key={g.id} value={g.id.toString()} className="font-semibold py-2.5 border-b border-gray-50 dark:border-white/5 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 text-xs text-black dark:text-white">
                                                                {g.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </>
                                    )}

                                    <div className="space-y-1.5 overflow-visible">
                                        <label className="text-[10px] uppercase font-black text-white/70 ml-1 tracking-wider">Subject</label>
                                        <Select
                                            value={selectedSubjectId ? selectedSubjectId.toString() : ''}
                                            onValueChange={(value) => {
                                                const subject = subjects.find(s => s.id.toString() === value);
                                                if (subject) {
                                                    updateSubject(subject.id, subject.label);
                                                    updateChapter(null, '');
                                                }
                                            }}
                                            disabled={loadingSubjects || (isTeacher && !selectedGradeId)}
                                        >
                                            <SelectTrigger className="w-full h-10 bg-white dark:bg-black/20 border-0 dark:border dark:border-white/10 text-black dark:text-white rounded-xl font-bold shadow-sm focus:ring-0 focus:ring-offset-0 text-xs">
                                                <SelectValue placeholder={loadingSubjects ? "Loading..." : "Select Subject"} />
                                            </SelectTrigger>
                                            <SelectContent className="z-[20000] bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-100 dark:border-white/10 rounded-xl shadow-xl max-h-[220px] overflow-y-auto">
                                                {subjects.map(subject => (
                                                    <SelectItem key={subject.id} value={subject.id.toString()} className="font-semibold py-2.5 border-b border-gray-50 dark:border-white/5 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 text-xs text-black dark:text-white">
                                                        {subject.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-1.5 overflow-visible">
                                        <label className="text-[10px] uppercase font-black text-white/70 ml-1 tracking-wider">Chapter</label>
                                        <Select
                                            value={selectedChapterId ? selectedChapterId.toString() : ''}
                                            onValueChange={(value) => {
                                                const chapter = chapters.find(c => c.id.toString() === value);
                                                if (chapter) {
                                                    updateChapter(chapter.id, chapter.label);
                                                }
                                            }}
                                            disabled={!selectedSubjectId}
                                        >
                                            <SelectTrigger className="w-full h-10 bg-white dark:bg-black/20 border-0 dark:border dark:border-white/10 text-black dark:text-white rounded-xl font-bold disabled:opacity-50 shadow-sm focus:ring-0 focus:ring-offset-0 text-xs">
                                                <SelectValue placeholder="Select Chapter" />
                                            </SelectTrigger>
                                            <SelectContent className="z-[20000] bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-100 dark:border-white/10 rounded-xl shadow-xl max-h-[220px] overflow-y-auto">
                                                {chapters.map(chapter => (
                                                    <SelectItem key={chapter.id} value={chapter.id.toString()} className="font-semibold py-2.5 border-b border-gray-50 dark:border-white/5 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 text-xs text-black dark:text-white">
                                                        {chapter.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <Button
                                        onClick={() => {
                                            if (canClose) {
                                                isUserOpenedRef.current = false;
                                                setIsSelectionExpanded(false);
                                            }
                                        }}
                                        disabled={!canClose}
                                        className="w-full h-11 bg-[hsl(var(--main-bg))] hover:bg-[hsl(var(--main-bg))]/90 text-[hsl(var(--foreground))] font-black text-sm rounded-xl mt-2 shadow-lg active:scale-[0.98] transition-all disabled:opacity-50"
                                    >
                                        Done
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}
        </>,
        document.body
    );
}
